import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Receipt, RefreshCw, Send, Check, Ban, Trash2, Download, FileDown, FileText, Pencil, X, Building, Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { crmApi, downloadFile, type CrmInvoice, type CrmQuote } from '../services/api';
import { useCrmSellerStore } from '../store/useCrmSellerStore';
import SellingAs from '../components/crm/SellingAs';

/**
 * Invoices, raised from accepted quotes.
 *
 * A draft has no number: the number is allocated when it is ISSUED, in the
 * issuing company's own sequence (HS-INV-…, W42-INV-…), so a deleted draft can
 * never leave a gap. Once issued an invoice is a record — it can be marked paid
 * or voided with a reason, never edited or deleted.
 */

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  issued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  void: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

function money(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((minor || 0) / 100);
  } catch {
    return `${((minor || 0) / 100).toFixed(2)} ${currency}`;
  }
}

export default function InvoicesPage() {
  const sellerOrgId = useCrmSellerStore((s) => s.sellerOrgId);
  const nameOf = useCrmSellerStore((s) => s.nameOf);
  const loadSellers = useCrmSellerStore((s) => s.loadSellers);
  const [invoices, setInvoices] = useState<CrmInvoice[]>([]);
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CrmInvoice | null>(null);
  const [downloadFor, setDownloadFor] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [params] = useSearchParams();
  const focus = params.get('focus') || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await crmApi.listInvoices({ sellerOrgId });
      setInvoices(r.invoices || []);
    } catch (err: any) {
      toast.error(err.message || 'Could not load invoices');
    } finally {
      setLoading(false);
    }
  }, [sellerOrgId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    loadSellers();
    crmApi.listClients(undefined, undefined, { includeArchived: true })
      .then((r) => setOrgNames(Object.fromEntries(
        (r.clients || []).filter((c) => c.orgId).map((c) => [c.orgId!.toUpperCase(), c.name]))))
      .catch(() => setOrgNames({}));
  }, [loadSellers]);

  // Quotes that already have a live invoice. Memoised: the picker refetches
  // when this changes, and a fresh Set every render would loop.
  const invoicedQuotes = useMemo(
    () => new Set(invoices.filter((i) => i.status !== 'void').map((i) => i.quoteId)),
    [invoices]);

  const outstanding = useMemo(
    () => invoices.filter((i) => i.status === 'issued').reduce((n, i) => n + (i.totalMinor || 0), 0),
    [invoices]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      await load();
    } catch (err: any) {
      toast.error(err.message || 'That did not work', { duration: 9000 });
    }
  };

  const issue = (i: CrmInvoice) => {
    if (!window.confirm('Issue this invoice?\n\nIt takes the next number in '
      + `${nameOf(i.sellerOrgId) || 'the company'}'s sequence and becomes a record: `
      + 'it can then only be marked paid or voided.')) return;
    void act(() => crmApi.issueInvoice(i._id), 'Invoice issued');
  };
  const pay = (i: CrmInvoice) => {
    const reference = window.prompt(`Payment received for ${i.number}.\nPayment reference (optional):`, '');
    if (reference === null) return;
    void act(() => crmApi.payInvoice(i._id, { reference }), 'Marked paid');
  };
  const voidIt = (i: CrmInvoice) => {
    const reason = window.prompt(`Void ${i.number || 'this draft'}? It keeps its number.\nReason:`, '');
    if (!reason) return;
    void act(() => crmApi.voidInvoice(i._id, reason), 'Invoice voided');
  };
  const name = (i: CrmInvoice) => i.number || `DRAFT-${i._id.split(':').pop()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Receipt size={20} className="text-accent" /> Invoices
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Raised from accepted quotes. Numbered when issued, per issuing company.
            {' '}Create one from the opportunity once the signed quote is uploaded.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SellingAs />
          <button onClick={() => setChoosing(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white">
            <Plus size={14} /> New invoice
          </button>
          <button onClick={() => void load()} title="Refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Outstanding (issued, unpaid)</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{money(outstanding)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Drafts</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {invoices.filter((i) => i.status === 'draft').length}
          </div>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Receipt size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No invoices yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            Upload the signed quote on an opportunity to accept it, then create the invoice there.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {invoices.map((i) => (
            <div key={i._id}
              className={`rounded-lg border bg-white dark:bg-gray-800 px-4 py-3 ${
                i._id === focus ? 'border-accent ring-1 ring-accent/40' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-gray-800 dark:text-gray-100">{i.number || 'Draft'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_STYLE[i.status] || ''}`}>{i.status}</span>
                    <span className="text-xs text-gray-400">from {i.quoteNumber} v{i.quoteVersion}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1"><Building size={11} /> {nameOf(i.sellerOrgId)}</span>
                    <span>bill to {orgNames[(i.billToOrgId || '').toUpperCase()] || i.billToOrgId}
                      {i.billToIsPartner ? ' (partner)' : ''}</span>
                    {i.poNumber && <span>PO {i.poNumber}</span>}
                    <span>{i.issueDate ? `issued ${i.issueDate} · due ${i.dueDate}` : `Net ${i.termsDays}`}</span>
                    {i.paidOn && <span>paid {i.paidOn}{i.paymentReference ? ` (${i.paymentReference})` : ''}</span>}
                    {i.voidReason && <span className="text-rose-500">void: {i.voidReason}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{money(i.totalMinor, i.currency)}</div>
                  <div className="flex items-center gap-1 justify-end mt-1.5">
                    {i.status === 'draft' && (
                      <>
                        <button onClick={() => setEditing(i)} title="Edit draft" className="p-1 rounded text-gray-400 hover:text-accent"><Pencil size={13} /></button>
                        <button onClick={() => issue(i)} title="Issue — allocates the number" className="p-1 rounded text-gray-400 hover:text-accent"><Send size={13} /></button>
                        <button onClick={() => { if (window.confirm('Delete this draft? It has no number yet.')) void act(() => crmApi.deleteInvoice(i._id), 'Draft deleted'); }}
                          title="Delete draft" className="p-1 rounded text-gray-400 hover:text-red-500"><Trash2 size={13} /></button>
                      </>
                    )}
                    {i.status === 'issued' && (
                      <>
                        <button onClick={() => pay(i)} title="Mark paid" className="p-1 rounded text-gray-400 hover:text-emerald-500"><Check size={13} /></button>
                        <button onClick={() => voidIt(i)} title="Void (keeps its number)" className="p-1 rounded text-gray-400 hover:text-rose-500"><Ban size={13} /></button>
                      </>
                    )}
                    <div className="relative">
                      <button onClick={() => setDownloadFor(downloadFor === i._id ? null : i._id)}
                        title="Download as PDF or Word" className="p-1 rounded text-gray-400 hover:text-accent"><Download size={13} /></button>
                      {downloadFor === i._id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setDownloadFor(null)} />
                          <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-left">
                            <button onClick={() => { setDownloadFor(null); void downloadFile(`/crm/invoices/${i._id}/export.pdf`, `${name(i)}.pdf`); }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700">
                              <FileDown size={13} className="text-accent" /> PDF
                            </button>
                            <button onClick={() => { setDownloadFor(null); void downloadFile(`/crm/invoices/${i._id}/export.docx`, `${name(i)}.docx`); }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700">
                              <FileText size={13} className="text-accent" /> Word
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {choosing && (
        <QuotePicker
          invoiced={invoicedQuotes}
          sellerOrgId={sellerOrgId}
          onClose={() => setChoosing(false)}
          onCreated={() => { setChoosing(false); void load(); }} />
      )}

      {editing && (
        <DraftEditor invoice={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }} />
      )}
    </div>
  );
}

function DraftEditor({ invoice, onClose, onSaved }: {
  invoice: CrmInvoice; onClose: () => void; onSaved: () => void;
}) {
  const [termsDays, setTermsDays] = useState(String(invoice.termsDays ?? 30));
  const [poNumber, setPoNumber] = useState(invoice.poNumber || '');
  const [notes, setNotes] = useState(invoice.notes || '');
  const [saving, setSaving] = useState(false);
  const field = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

  const save = async () => {
    setSaving(true);
    try {
      await crmApi.updateInvoice(invoice._id, { termsDays: Number(termsDays) || 0, poNumber, notes });
      toast.success('Draft saved');
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Draft invoice — {invoice.quoteNumber}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Payment terms (days)</label>
              <input className={field} inputMode="numeric" value={termsDays}
                onChange={(e) => setTermsDays(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            <div>
              <label className={label}>Purchase order</label>
              <input className={field} maxLength={60} value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label}>Notes (printed on the invoice)</label>
            <textarea className={field} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <p className="text-[11px] text-gray-400">
            Lines and totals come from the accepted quote and cannot be changed here.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">Cancel</button>
          <button onClick={() => void save()} disabled={saving} className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Accepted quotes with no live invoice: the ones that can be billed now. */
function QuotePicker({ invoiced, sellerOrgId, onClose, onCreated }: {
  invoiced: Set<string>; sellerOrgId: string; onClose: () => void; onCreated: () => void;
}) {
  const nameOf = useCrmSellerStore((s) => s.nameOf);
  const [quotes, setQuotes] = useState<CrmQuote[] | null>(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    crmApi.listQuotes(undefined, sellerOrgId)
      .then((r) => setQuotes((r.quotes || []).filter((q) => q.status === 'accepted' && !invoiced.has(q._id))))
      .catch(() => setQuotes([]));
  }, [invoiced, sellerOrgId]);

  const create = async (q: CrmQuote) => {
    setBusy(q._id);
    try {
      await crmApi.createInvoice(q._id);
      toast.success(`Draft invoice created for ${q.quoteNumber} — issue it to number it`);
      onCreated();
    } catch (err: any) {
      toast.error(err.message || 'Could not create the invoice', { duration: 9000 });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invoice an accepted quote</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          {quotes === null ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : quotes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No accepted quote is waiting to be invoiced. A quote is accepted when its signed copy
              is uploaded on the opportunity (or with Mark accepted on Quotes).
            </p>
          ) : quotes.map((q) => (
            <button key={q._id} onClick={() => void create(q)} disabled={!!busy}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-left hover:border-accent/50 disabled:opacity-50">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {q.quoteNumber} v{q.version} — {q.title || '(untitled)'}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {q.sellerOrgId ? `sold by ${nameOf(q.sellerOrgId)}` : 'no selling company set'}
                  {q.poNumber ? ` · PO ${q.poNumber}` : ''}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {busy === q._id ? 'Creating…' : money(q.totalMinor, q.currency)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
